import React from "react";
import { Card, CardContent } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import {
  ShieldCheck,
  TrendingUp,
  DollarSign,
  Percent,
  Info,
} from "lucide-react";
import { cn } from "@shared/lib/utils";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";

function SecurityDepositNoInterest({ rule }: { rule: any }) {
  const currency = useCurrency();
  const amountVal = Number(rule?.amount ?? 0);
  const amount = `${currency} ${amountVal.toFixed(2)}`;
  const eligibleAfter = rule?.eligibleAfter ?? rule?.eligible_after ?? 0;
  const eligibleUnit =
    rule?.eligibleAfterUnit || rule?.eligible_after_unit || "months";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <QuickStatsCard
        title="Required Amount"
        count={amount}
        icon={<DollarSign className="h-8 w-8 text-blue-500" />}
      />
      <QuickStatsCard
        title="Basis"
        count={rule?.basis ? rule.basis.toUpperCase() : "N/A"}
        icon={<Percent className="h-8 w-8 text-purple-500" />}
      />
      <QuickStatsCard
        title="Refund Eligibility"
        count={
          eligibleAfter > 0
            ? `After ${eligibleAfter} ${eligibleUnit}`
            : "Immediate"
        }
        icon={<ShieldCheck className="h-8 w-8 text-green-500" />}
      />
    </div>
  );
}

function SecurityDepositWithInterest({ rule }: { rule: any }) {
  const currency = useCurrency();
  const amountVal = Number(rule?.amount ?? 0);
  const amount = `${currency} ${amountVal.toFixed(2)}`;
  const interestTiers = rule?.interestTiers || rule?.interest_tiers || [];
  const primaryRate =
    interestTiers.length > 0 ? `${interestTiers[0].rate ?? 0}% p.a.` : "N/A";
  const eligibleAfter = rule?.eligibleAfter ?? rule?.eligible_after ?? 0;
  const eligibleUnit =
    rule?.eligibleAfterUnit || rule?.eligible_after_unit || "months";

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <QuickStatsCard
          title="Required Amount"
          count={amount}
          icon={<DollarSign className="h-8 w-8 text-blue-500" />}
        />
        <QuickStatsCard
          title="Interest Rate"
          count={primaryRate}
          icon={<TrendingUp className="h-8 w-8 text-green-500" />}
        />
        <QuickStatsCard
          title="Basis"
          count={rule?.basis ? rule.basis.toUpperCase() : "N/A"}
          icon={<Percent className="h-8 w-8 text-purple-500" />}
        />
        <QuickStatsCard
          title="Refund Eligibility"
          count={
            eligibleAfter > 0
              ? `After ${eligibleAfter} ${eligibleUnit}`
              : "Immediate"
          }
          icon={<ShieldCheck className="h-8 w-8 text-green-500" />}
        />
      </div>

      {interestTiers.length > 0 && (
        <div className="bg-gray-50 text-gray-600 border border-gray-100 rounded-xl p-3 text-xs flex items-center gap-2 font-medium mb-2">
          <Info className="w-3.5 h-3.5 text-gray-400" />
          Interest Tiers:{" "}
          {interestTiers
            .map(
              (tTier: any) =>
                `${tTier.rate}% (${tTier.fromMonth || 0} to ${tTier.toMonth || 0} months)`,
            )
            .join(", ")}
        </div>
      )}
    </div>
  );
}

export function SecurityDeposit({ planConfig }: { planConfig?: any }) {
  const resData = planConfig?.result || planConfig;
  const deposits = resData?.deposits || planConfig?.deposits;
  const rules = Array.isArray(deposits?.rules)
    ? deposits.rules
    : deposits?.rule
      ? [deposits.rule]
      : resData?.depositAmount
        ? [
            {
              amount: resData.depositAmount,
              basis: "Fixed Standard Amount",
              payInterest: false,
            },
          ]
        : [];
  const primaryRule = rules[0];

  const isInterestBearing =
    primaryRule?.payInterest === true ||
    primaryRule?.pay_interest === true ||
    String(
      primaryRule?.payInterest ?? primaryRule?.pay_interest,
    ).toLowerCase() === "true";
  const isEnabled =
    deposits?.enabled === true ||
    String(deposits?.enabled).toLowerCase() === "true" ||
    !!resData?.depositAmount;

  if (!isEnabled || !rules || rules.length === 0) {
    return null;
  }

  return (
    <Card className="shadow-sm border-gray-200 bg-white rounded-xl overflow-hidden mb-6">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3.5 mb-4 pb-4 border-b border-gray-100">
          <div className="w-9 h-9 rounded-xl bg-emerald-50/80 text-emerald-600 flex items-center justify-center border border-emerald-100/50">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="text-base font-semibold text-gray-900">
                Security Deposit
              </h3>
              <Badge
                variant="secondary"
                className={cn(
                  "border rounded-full font-semibold text-xs px-2 py-0 flex items-center gap-1",
                  isInterestBearing
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-50",
                )}
              >
                {isInterestBearing ? (
                  <>
                    <TrendingUp className="w-3 h-3" /> Interest-Bearing
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-3 h-3" /> Non-Interest Bearing
                  </>
                )}
              </Badge>
            </div>
            <p className="text-xs text-gray-500 font-medium">
              {primaryRule?.depositName || "N/A"}
            </p>
          </div>
          <div className="ml-auto">
            <Badge
              variant="secondary"
              className="bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 font-semibold px-2.5 py-0.5 rounded-lg text-xs"
            >
              Active Rule
            </Badge>
          </div>
        </div>

        {/* Dynamic Content */}
        {isInterestBearing ? (
          <SecurityDepositWithInterest rule={primaryRule} />
        ) : (
          <SecurityDepositNoInterest rule={primaryRule} />
        )}
      </CardContent>
    </Card>
  );
}
